/**
 * Hydra checkout intercept — redirects Shopify checkout to Whop.
 * Include on the preview theme via:
 *   <script src="{{ 'hydra-checkout.js' | asset_url }}" defer
 *           data-hydra-endpoint="https://<worker-url>/checkout"></script>
 * Fail-open: any error falls through to Shopify's native checkout.
 *
 * Covers three checkout paths in this theme:
 *   1. Standard cart page / notification buttons (name="checkout", /checkout links)
 *   2. The fishy bundle widget (<fish-bundle>), which normally checks out via a
 *      cart-permalink JS redirect — its submit() is patched here
 *   3. Wallet express buttons (Shop Pay / Apple Pay / Google Pay) can't be
 *      intercepted, so they are hidden while Hydra is active
 *
 * Pixel handling: fires InitiateCheckout on TikTok (ttq) and Facebook (fbq)
 * before redirecting, and ships click IDs / cart attributes / discount code to
 * the Worker so the written-back order matches a native checkout purchase.
 */
(function () {
  var script = document.currentScript;
  var ENDPOINT =
    (script && script.getAttribute("data-hydra-endpoint")) ||
    (window.HYDRA_CHECKOUT_ENDPOINT || "");
  if (!ENDPOINT) return;

  var busy = false;

  /* Hide un-interceptable wallet express buttons so every path goes to Whop. */
  var css = document.createElement("style");
  css.textContent =
    "shopify-accelerated-checkout, shopify-accelerated-checkout-cart," +
    ".shopify-payment-button, .additional-checkout-buttons, #dynamic-checkout-cart" +
    "{ display: none !important; }";
  document.head.appendChild(css);

  function getCookie(name) {
    var m = document.cookie.match("(?:^|; )" + name + "=([^;]*)");
    return m ? decodeURIComponent(m[1]) : "";
  }

  // The TikTok pixel an ad belongs to rides on the ad URL as ?ttpx=<pixel id>; keep it for a year
  // so the checkout can route its server-side events to that exact pixel. Stored in a cookie AND
  // localStorage (2026-09-07: ~16% of ad-click sessions reached checkout without the cookie), and
  // the campaign id (utm_id) is kept the same way so the worker can map campaign → pixel.
  function remember(name, value, re) {
    if (!value || !re.test(value)) return;
    try { document.cookie = name + "=" + encodeURIComponent(value) + ";max-age=31536000;path=/;samesite=lax"; } catch (e) {}
    try { window.localStorage.setItem(name, value); } catch (e) {}
  }
  function recall(name) {
    var v = getCookie(name);
    if (!v) { try { v = window.localStorage.getItem(name) || ""; } catch (e) { v = ""; } }
    return v;
  }
  try {
    var qs0 = new URLSearchParams(window.location.search);
    remember("_hydra_ttpx", qs0.get("ttpx") || qs0.get("pixel_id"), /^[A-Z0-9]{10,30}$/i);
    remember("_hydra_utm_id", qs0.get("utm_id"), /^\d{6,}$/);
    remember("_hydra_ttclid", qs0.get("ttclid"), /^[\w.-]{8,600}$/);
  } catch (e) {}

  function collectTracking() {
    var qs = new URLSearchParams(window.location.search);
    return {
      ttpx: qs.get("ttpx") || qs.get("pixel_id") || recall("_hydra_ttpx") || "",
      utm_id: qs.get("utm_id") || recall("_hydra_utm_id") || "",
      ttclid: qs.get("ttclid") || recall("_hydra_ttclid") || getCookie("_ttclid") || getCookie("ttclid") || "",
      ttp: getCookie("_ttp") || "",
      fbp: getCookie("_fbp") || "",
      fbc: getCookie("_fbc") || "",
      landing: window.location.href.slice(0, 500),
      referrer: document.referrer.slice(0, 500),
    };
  }

  /* ------------------------------------------------------------------------------------------
   * TikTok browser pixel (2026-09-10). WeTracked's storefront script used to load it and fire
   * PageView / ViewContent / AddToCart; its pixels were detached (it double-counted purchases), so the
   * theme loads the pixel itself. Which pixel: the ad's (?ttpx=, remembered for a year) or the store
   * default from data-hydra-pixel. InitiateCheckout / CompletePayment stay server-side in the worker
   * (the browser InitiateCheckout below shares the worker's event_id, so TikTok merges the pair).
   * ---------------------------------------------------------------------------------------- */
  // No store default pixel (owner rule 2026-09-11): only the ad's own pixel (?ttpx=, remembered) — data-hydra-pixel is ignored.
  var PIXEL = "";
  try { PIXEL = String(collectTracking().ttpx || "").toUpperCase(); } catch (e) { PIXEL = ""; }
  // 2026-09-11: WeTracked is attached again for the pixels in data-hydra-wt-pixels and fires their PageView / ViewContent /
  // AddToCart itself (and their purchases; the worker skips CompletePayment for them via KV tiktok_pixels.wetracked_pixels).
  // The theme only covers pixels WeTracked does not hold, and fires on that pixel's instance so nothing lands twice.
  var WT_PIXELS = String((script && script.getAttribute("data-hydra-wt-pixels")) || "").toUpperCase().split(/[\s,]+/).filter(Boolean);
  if (PIXEL && WT_PIXELS.indexOf(PIXEL) !== -1) PIXEL = "";
  function loadTikTok(pixel) {
    if (!pixel || !/^[A-Z0-9]{10,30}$/.test(pixel)) return;
    if (window.ttq && window.ttq._i && window.ttq._i[pixel]) return; // already loaded (e.g. by another app)
    !(function (w, d, t) {
      w.TiktokAnalyticsObject = t; var ttq = (w[t] = w[t] || []);
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
      ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
      for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]); return e; };
      ttq.load = function (e, n) {
        var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = r; ttq._t = ttq._t || {}; ttq._t[e] = +new Date(); ttq._o = ttq._o || {}; ttq._o[e] = n || {};
        var s = d.createElement("script"); s.type = "text/javascript"; s.async = true; s.src = r + "?sdkid=" + e + "&lib=" + t;
        var f = d.getElementsByTagName("script")[0]; f.parentNode.insertBefore(s, f);
      };
    })(window, document, "ttq");
    window.ttq.load(pixel);
    window.ttq.instance(pixel).page();
  }
  function currency() { try { return (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD"; } catch (e) { return "USD"; } }
  function productMeta() { try { return (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) || null; } catch (e) { return null; } }
  function variantOf(id) {
    var m = productMeta(); if (!m || !m.variants) return null;
    for (var i = 0; i < m.variants.length; i++) if (String(m.variants[i].id) === String(id)) return m.variants[i];
    return null;
  }

  // One ID per real action, shared by browser and first-party server delivery.
  function actionId() { return window.crypto && crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "") : Date.now().toString(36) + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2); }
  var backupKey = "_hydra_event_queue_v1";
  function readPending() { try { return JSON.parse(localStorage.getItem(backupKey) || "[]").filter(function (e) { return Date.now() - e.at < 3600000; }).slice(-30); } catch (e) { return []; } }
  function savePending(q) { try { localStorage.setItem(backupKey, JSON.stringify(q.slice(-30))); } catch (e) {} }
  var sending = {};
  function sendBackup(entry) {
    if (sending[entry.id]) return; sending[entry.id] = true;
    fetch(new URL("/events", ENDPOINT).href, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(entry), keepalive: true })
      .then(function (r) { if (r.status >= 500 || r.status === 429) throw new Error("retry"); return r.json(); })
      .then(function (r) { if (r.accepted || r.queued || r.skipped || r.error) savePending(readPending().filter(function (e) { return e.id !== entry.id; })); })
      .catch(function () {}).then(function () { delete sending[entry.id]; });
  }
  function serverTrack(event, id, items) {
    if (!PIXEL || !items.length) return;
    var entry = { event: event, id: id, at: Date.now(), tracking: collectTracking(), items: items.map(function (i) { return { variant_id: String(i.variant_id), quantity: i.quantity || 1 }; }) };
    entry.tracking.ttpx = PIXEL;
    savePending(readPending().concat([entry])); sendBackup(entry);
  }
  function flushPending() { readPending().forEach(sendBackup); }
  window.addEventListener("online", flushPending);
  window.addEventListener("pageshow", flushPending);
  setTimeout(flushPending, 1000);
  setTimeout(flushPending, 15000);
  var lastSavedTracking = "";
  function persistTracking() {
    var t = collectTracking(), attrs = {};
    ["ttpx", "utm_id", "ttclid", "ttp"].forEach(function (k) { if (t[k]) attrs[k] = t[k]; });
    if (!attrs.ttpx && !attrs.ttclid && !attrs.utm_id) return;
    var signature = JSON.stringify(attrs); if (lastSavedTracking === signature) return; lastSavedTracking = signature;
    fetch((window.Shopify && Shopify.routes && Shopify.routes.root || "/") + "cart/update.js", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attributes: attrs }), keepalive: true }).catch(function () { lastSavedTracking = ""; });
  }
  function ttTrack(name, payload, items) {
    if (!PIXEL) return;
    var id = actionId();
    try { if (window.ttq && typeof window.ttq.instance === "function") window.ttq.instance(PIXEL).track(name, payload, { event_id: id + ":" + name }); } catch (e) {}
    serverTrack(name, id, items || []); persistTracking();
  }
  function fireViewContent() {
    var m = productMeta(); if (!m) return;
    var v = null;
    try { v = variantOf(ShopifyAnalytics.meta.selectedVariantId) || (m.variants && m.variants[0]) || null; } catch (e) {}
    var price = v && v.price != null ? Number(v.price) / 100 : undefined;
    ttTrack("ViewContent", { contents: [{ content_id: String(m.id), content_type: "product", content_name: (v && v.name) || m.type || "", price: price, quantity: 1 }], value: price, currency: currency() }, v && v.id ? [{ variant_id: v.id, quantity: 1 }] : []);
  }
  var atcSeen = {};
  function fireAddToCart(items) {
    // items: [{variant_id, product_id, quantity, price (cents), title}]
    var value = 0, contents = [], eventItems = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i]; var key = it.variant_id + ":" + Math.floor(Date.now() / 3000); // one AddToCart per variant per 3 s
      if (atcSeen[key]) continue; atcSeen[key] = 1; eventItems.push(it);
      var price = it.price != null ? Number(it.price) / 100 : undefined;
      if (price) value += price * (it.quantity || 1);
      contents.push({ content_id: String(it.product_id || it.variant_id), content_type: "product", content_name: it.title || "", price: price, quantity: it.quantity || 1 });
    }
    if (contents.length) ttTrack("AddToCart", { contents: contents, value: value || undefined, currency: currency() }, eventItems);
  }
  function itemsFromCartAddResponse(json) {
    var arr = json && json.items ? json.items : json ? [json] : [];
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      var it = arr[i]; if (!it || !it.variant_id) continue;
      // add-on apps (Shipping Protection / Priority Processing) auto-add their own line right after the real one:
      // not a product the shopper chose, so it never counts as an AddToCart (same rule as the worker's reports)
      if (/shipping protection|priority processing|route package/i.test(String(it.product_title || it.title || ""))) continue;
      out.push({ variant_id: it.variant_id, product_id: it.product_id, quantity: it.quantity || 1, price: it.price, title: it.product_title || it.title });
    }
    return out;
  }
  function isCartAdd(url) { return typeof url === "string" && /\/cart\/add(\.js|\.json)?(\?|$)/.test(url); }
  function isCartChange(url) { return typeof url === "string" && /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(url); }

  /* ------------------------------------------------------------------------------------------
   * Shipping in the cart (2026-09-11, owner: "make sure it's known in the cart" — not on product pages): a
   * "Tracked shipping" + "Total" pair right above every cart checkout button (.cart__ctas, drawer and /cart), using
   * the worker's /shipping-rate = the same default rate the checkout is minted with, so the pay-page total matches.
   * /cart.js is re-read only after real cart changes; DOM re-renders (drawer refresh, countdown) just re-insert.
   * ---------------------------------------------------------------------------------------- */
  var SHIP = null, shipReq = null, shipData = null, shipTimer = 0;
  function shipFmt(c, cur) { try { return new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(c / 100); } catch (e) { return "$" + (c / 100).toFixed(2); } }
  function loadShip() {
    if (SHIP) return Promise.resolve(SHIP);
    if (!shipReq) shipReq = fetch(new URL(ENDPOINT, location.href).origin + "/shipping-rate").then(function (r) { return r.json(); }).then(function (d) { if (d && typeof d.cents === "number") SHIP = d; return SHIP; }).catch(function () { shipReq = null; return null; });
    return shipReq;
  }
  // The theme's own visible "Subtotal" row near this checkout button: our rows are clones of it, so fonts and
  // spacing match the theme. Hierarchy (owner 2026-09-11): Total is the big bold line; Subtotal drops to the
  // regular line size like shipping.
  function subtotalRow(scope) {
    var els = scope.querySelectorAll("strong, span, dt, th, p, div, h2, h3, h4");
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.children.length || !/^\s*Subtotal\s*$/i.test(el.textContent) || el.offsetParent === null) continue;
      var row = el.parentElement;
      while (row && row !== scope && !/\d[.,]\d\d/.test(row.textContent)) row = row.parentElement;
      if (row && row !== scope) return row;
    }
    return null;
  }
  var SMALL = "1.6rem";
  function setRow(row, label, money, bold, size) {
    [].forEach.call(row.querySelectorAll("[id]"), function (n) { n.removeAttribute("id"); }); row.removeAttribute("id");
    var kids = row.children;
    if (kids.length >= 2) {
      kids[0].innerHTML = bold ? "<strong>" + label + "</strong>" : label;
      kids[kids.length - 1].innerHTML = bold ? "<strong>" + money + "</strong>" : money;
    } else row.textContent = label + "  " + money;
    if (size) row.style.setProperty("--text-size", size);
    row.style.fontWeight = bold ? "" : "400";
  }
  function demoteSubtotal(sub) {
    if (!sub || sub.getAttribute("data-hydra-demoted")) return;
    sub.setAttribute("data-hydra-size", sub.style.getPropertyValue("--text-size") || "");
    sub.setAttribute("data-hydra-demoted", "1");
    sub.style.setProperty("--text-size", SMALL);
    [].forEach.call(sub.querySelectorAll("strong, b"), function (n) { n.style.fontWeight = "400"; });
  }
  function shipBoxes() {
    var ctas = [].slice.call(document.querySelectorAll(".cart__ctas")).filter(function (el) { return el.querySelector('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'); });
    ctas.forEach(function (cta) {
      var prev = cta.previousElementSibling, box = prev && prev.className === "hydra-ship" ? prev : null;
      if (!shipData || shipData.empty) { if (box) box.parentNode.removeChild(box); return; }
      var scope = cta.closest("cart-drawer, .cart-drawer, #CartDrawer, .cart__footer, .cart__blocks, form, main") || document.body;
      var sub = subtotalRow(scope);
      var key = shipData.ship + "|" + shipData.total + "|" + (sub ? 1 : 0);
      if (!box) { box = document.createElement("div"); box.className = "hydra-ship"; cta.parentNode.insertBefore(box, cta); }
      if (box.getAttribute("data-k") !== key) {
        box.setAttribute("data-k", key); box.innerHTML = "";
        if (sub) {
          var big = sub.getAttribute("data-hydra-size") || sub.style.getPropertyValue("--text-size") || "";
          var shipRow = sub.cloneNode(true), totRow = sub.cloneNode(true);
          [shipRow, totRow].forEach(function (r) { r.removeAttribute("data-hydra-demoted"); r.removeAttribute("data-hydra-size"); [].forEach.call(r.querySelectorAll("strong, b"), function (n) { n.style.fontWeight = ""; }); });
          setRow(shipRow, "Tracked shipping (insured)", shipData.ship, false, SMALL);
          setRow(totRow, "Total", shipData.total, true, big);
          // same spacing as the theme's rows: its totals list is a flex column with a gap (or margins between rows)
          var pcs = getComputedStyle(sub.parentElement), gap = parseFloat(pcs.rowGap);
          if (!(gap > 0)) { var pr = sub.previousElementSibling; gap = pr ? Math.max(0, sub.getBoundingClientRect().top - pr.getBoundingClientRect().bottom) : 8; }
          box.style.cssText = "display:flex;flex-direction:column;gap:" + Math.round(gap || 8) + "px;margin-top:" + Math.round(gap || 8) + "px";
          box.appendChild(shipRow); box.appendChild(totRow);
        } else {
          var row = "display:flex;justify-content:space-between;gap:12px;margin:6px 0;";
          box.innerHTML = '<div style="' + row + '"><span>Tracked shipping (insured)</span><span>' + shipData.ship + '</span></div>' +
                          '<div style="' + row + 'font-weight:700;font-size:1.25em"><span>Total</span><span>' + shipData.total + '</span></div>';
        }
      }
      demoteSubtotal(sub);
    });
  }
  function refreshShip() {
    clearTimeout(shipTimer);
    shipTimer = setTimeout(function () {
      Promise.all([loadShip(), fetch("/cart.js", { credentials: "same-origin" }).then(function (r) { return r.json(); })]).then(function (res) {
        var ship = res[0], cart = res[1]; if (!ship || !cart) return;
        shipData = cart.item_count ? { ship: shipFmt(ship.cents, cart.currency), total: shipFmt(cart.total_price + ship.cents, cart.currency) } : { empty: true };
        shipBoxes();
      }).catch(function () {});
    }, 300);
  }
  if (window.MutationObserver) {
    var shipObsT = 0;
    new MutationObserver(function () { if (!shipData) return; clearTimeout(shipObsT); shipObsT = setTimeout(shipBoxes, 120); }).observe(document.documentElement, { childList: true, subtree: true });
  }
  function shipInit() { if (document.querySelector(".cart__ctas")) refreshShip(); } // a cart with items is on this page (drawer or /cart)
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", shipInit); else shipInit();
  var lastCartAdd = 0; // when the theme's own AJAX add ran: the form-submit fallback below then stays quiet
  /* ------------------------------------------------------------------------------------------
   * Checkout preload (2026-09-11): after an add-to-cart, once the page is idle, load the worker's /prewarm page
   * (Whop's checkout app in a hidden frame) so the ~700 requests of Whop's page are already cached when the buyer
   * taps checkout (card form 4 s cold → ~1–1.5 s). Browsers partition the cache per top-level SITE, so this only
   * helps when the checkout endpoint is on the store's own site (checkout.<store>.com); otherwise it is skipped.
   * ---------------------------------------------------------------------------------------- */
  var prewarmed = false;
  function siteOf(host) { var p = String(host || "").toLowerCase().split("."); return p.slice(-2).join("."); }
  function prewarmCheckout() {
    if (prewarmed) return; prewarmed = true;
    try {
      var base = new URL(ENDPOINT, location.href);
      if (siteOf(base.hostname) !== siteOf(location.hostname)) return;
      var go = function () {
        var f = document.createElement("iframe");
        f.src = base.origin + "/prewarm"; f.title = ""; f.tabIndex = -1; f.setAttribute("aria-hidden", "true");
        f.style.cssText = "position:absolute;left:-10000px;top:0;width:390px;height:900px;border:0;opacity:0;pointer-events:none";
        document.body.appendChild(f);
      };
      if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 2500 }); else setTimeout(go, 1200);
    } catch (e) {}
  }
  if (/^\/cart\/?$/.test(location.pathname)) setTimeout(prewarmCheckout, 1500); // landed on the cart page = about to check out
  // Shopify themes add to cart with fetch (Shrine) or XHR; both are wrapped (AddToCart pixel event only when this
  // page owns a pixel; the preload always), and the plain form submit is the fallback.
  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var p = _fetch.apply(this, arguments);
      if (isCartAdd(url)) lastCartAdd = Date.now();
      if (isCartChange(url)) p.then(function () { refreshShip(); }).catch(function () {});
      if (isCartAdd(url)) p.then(function (res) { try { if (res.ok) { prewarmCheckout(); if (PIXEL) res.clone().json().then(function (j) { fireAddToCart(itemsFromCartAddResponse(j)); }).catch(function () {}); } } catch (e) {} }).catch(function () {});
      return p;
    };
  }
  if (window.XMLHttpRequest) {
    var _open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { if (isCartChange(String(url))) this.addEventListener("load", function () { refreshShip(); }); if (isCartAdd(String(url))) lastCartAdd = Date.now(); if (isCartAdd(String(url))) this.addEventListener("load", function () { try { if (this.status >= 200 && this.status < 300) { prewarmCheckout(); if (PIXEL) fireAddToCart(itemsFromCartAddResponse(JSON.parse(this.responseText))); } } catch (e) {} }); } catch (e) {}
      return _open.apply(this, arguments);
    };
  }
  document.addEventListener("submit", function (e) {
    try {
      var f = e.target; if (!f || !f.action || !isCartAdd(f.action)) return;
      prewarmCheckout(); if (!PIXEL) return;
      var id = f.querySelector('[name="id"]'); var q = f.querySelector('[name="quantity"]');
      var v = id ? variantOf(id.value) : null; var m = productMeta(); var at = Date.now();
      // fallback for non-AJAX themes only: if the theme's fetch/XHR add fires within 1.5 s, that path reports it
      if (id) setTimeout(function () {
        if (lastCartAdd >= at - 200) return;
        fireAddToCart([{ variant_id: id.value, product_id: m && m.id, quantity: q ? Number(q.value) || 1 : 1, price: v && v.price, title: v && v.name }]);
      }, 1500);
    } catch (err) {}
  }, true);
  function startPixel() {
    if (WT_PIXELS.indexOf(PIXEL) !== -1) PIXEL = "";
    if (!PIXEL) return;
    loadTikTok(PIXEL); persistTracking();
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fireViewContent); else fireViewContent();
  }
  persistTracking();
  if (PIXEL) startPixel();
  else fetch((window.Shopify && Shopify.routes && Shopify.routes.root || "/") + "cart.js", { credentials: "same-origin" }).then(function (r) { return r.json(); }).then(function (cart) {
    var a = cart.attributes || {};
    // Recover only saved ad attribution, never guess a pixel from the product being viewed.
    if (!collectTracking().ttpx && /^[A-Z0-9]{10,30}$/i.test(a.ttpx || "")) {
      remember("_hydra_ttpx", a.ttpx, /^[A-Z0-9]{10,30}$/i);
      remember("_hydra_utm_id", a.utm_id || "", /^\d{6,}$/);
      remember("_hydra_ttclid", a.ttclid || "", /^[\w.-]{8,2048}$/);
      PIXEL = String(a.ttpx).toUpperCase(); startPixel();
    }
  }).catch(function () {});

  // TikTok: the worker also sends InitiateCheckout server-side with event_id = session id, so the
  // browser event carries the same id and TikTok deduplicates the pair (without it, every checkout
  // counted twice — seen 2026-09-07).
  function firePixels(valueCents, currency, items, eventId) {
    try {
      // 2026-09-11: only the ad's own pixel (?ttpx=) — ttq.track() would hit every pixel WeTracked loaded on the page.
      // No ad pixel → no browser event; the worker's server InitiateCheckout (campaign/product map) still covers it.
      var adPx = ""; try { adPx = String(collectTracking().ttpx || "").toUpperCase(); } catch (e2) {}
      if (adPx && window.ttq && typeof window.ttq.instance === "function") {
        window.ttq.instance(adPx).track("InitiateCheckout", {
          value: valueCents / 100,
          currency: currency,
          contents: items.map(function (i) {
            return { content_id: String(i.variant_id), content_type: "product", quantity: i.quantity };
          }),
        }, eventId ? { event_id: eventId + ":InitiateCheckout" } : undefined);
      }
    } catch (e) {}
    try {
      if (window.fbq && typeof window.fbq === "function") {
        window.fbq("track", "InitiateCheckout", { value: valueCents / 100, currency: currency });
      }
    } catch (e) {}
  }

  /**
   * Send items to the Worker, redirect to the returned Whop purchase_url.
   * fallback() runs on any failure so the native path still completes the sale.
   */
  /* Tell the worker when we hand a buyer to Shopify's native checkout (the leak is measured in /ops/api/funnel). */
  function reportFallback(stage, reason, items) {
    try {
      var u = ENDPOINT.replace(/\/checkout\/?$/, "") + "/checkout/fallback";
      var body = JSON.stringify({ stage: stage, reason: String(reason || "").slice(0, 200), url: window.location.href.slice(0, 300), items: items ? items.length : 0 });
      if (navigator.sendBeacon) navigator.sendBeacon(u, new Blob([body], { type: "text/plain" }));
      else fetch(u, { method: "POST", body: body, headers: { "Content-Type": "text/plain" }, keepalive: true }).catch(function () {});
    } catch (e) {}
  }

  function hydraCheckout(items, extras, fallback) {
    if (busy) return;
    busy = true;
    // WeTracked's external-checkout matching needs the ORIGINAL storefront cart token,
    // not the separate Storefront API cart the worker creates to calculate prices.
    // Standard checkout already read the cart. Direct-buy bundles need its attribution
    // too, but must retain their selected items rather than purchasing the existing cart.
    var cartContext = extras && extras.cart ? Promise.resolve(extras.cart) : new Promise(function (resolve) {
      var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      var timer = setTimeout(function () { if (controller) controller.abort(); resolve(null); }, 1500);
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      fetch(root + "cart.js", { credentials: "same-origin", signal: controller ? controller.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error("cart context unavailable"); return r.json(); })
        .then(function (cart) { clearTimeout(timer); resolve(cart); })
        .catch(function () { clearTimeout(timer); resolve(null); });
    });
    cartContext.then(function (cart) {
      var attributes = Object.assign({}, (cart && cart.attributes) || {}, (extras && extras.attributes) || {});
      if (cart && typeof cart.token === "string" && cart.token) attributes["shopify-cart-token"] = cart.token;
      var payload = {
        items: items,
        note: (extras && extras.note) || (cart && cart.note) || "",
        attributes: attributes,
        discount_code: (extras && extras.discount_code) || "",
        tracking: collectTracking(),
      };
      return fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.purchase_url) {
          firePixels(extras && extras.value_cents ? extras.value_cents : 0, (extras && extras.currency) || "USD", items, data.id);
          window.location.href = data.purchase_url;
        } else {
          throw new Error((data && data.error) || "no purchase_url");
        }
      })
      .catch(function (err) {
        if (window.console) console.warn("[hydra] falling back to native checkout:", err);
        reportFallback("worker", err && (err.message || err), items);
        busy = false;
        fallback();
      });
  }

  /* ---- Path 1: standard checkout buttons and links ---- */

  function isCheckoutTrigger(el) {
    if (!el || !el.closest) return null;
    return el.closest(
      'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-hydra-checkout]'
    );
  }

  function interceptStandard(e) {
    var trigger = isCheckoutTrigger(e.target);
    if (!trigger || busy) return;
    e.preventDefault();
    e.stopPropagation();
    setLoading(trigger, true);
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) throw new Error("empty cart");
        hydraCheckout(
          cart.items.map(function (i) { return { variant_id: i.variant_id, quantity: i.quantity, properties: i.properties || undefined }; }),
          { cart: cart, note: cart.note || "", attributes: cart.attributes || {}, value_cents: cart.total_price, currency: cart.currency },
          function () { window.location.href = "/checkout"; }
        );
      })
      .catch(function (err) {
        reportFallback("cart", err && (err.message || err));
        setLoading(trigger, false);
        window.location.href = "/checkout";
      });
  }

  /* Early-click guard (inline in theme.liquid <head>, see scripts/theme-early-guard.py): a checkout tap that
     happened before this file loaded was held back as window.__hydraPending — replay it now. */
  try {
    window.__hydraReady = true;
    if (window.__hydraPending) {
      var pendingEl = window.__hydraPending; window.__hydraPending = null;
      interceptStandard({ target: pendingEl, preventDefault: function () {}, stopPropagation: function () {} });
    }
  } catch (e) {}

  function setLoading(el, on) {
    try {
      el.style.opacity = on ? "0.6" : "";
      if (el.tagName === "BUTTON" || el.tagName === "INPUT") el.disabled = on;
    } catch (e) {}
  }

  document.addEventListener("click", interceptStandard, true);
  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (form && /\/cart/.test(form.getAttribute("action") || "") &&
        (e.submitter && e.submitter.name === "checkout")) {
      interceptStandard({ target: e.submitter, preventDefault: function(){ e.preventDefault(); }, stopPropagation: function(){ e.stopPropagation(); } });
    }
  }, true);

  /* ---- Path 2: fishy bundle widget (direct-checkout permalink) ---- */

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined("fish-bundle").then(function () {
      var FB = customElements.get("fish-bundle");
      if (!FB || !FB.prototype || !FB.prototype.submit) return;
      var origSubmit = FB.prototype.submit;
      FB.prototype.submit = function () {
        var self = this;
        try {
          var mode = this.dataset.checkoutMode || "checkout";
          if (mode === "cart") return origSubmit.call(this); // ajax add-to-cart path: untouched
          var p = this.pricing();
          if (!p || !p.complete) return origSubmit.call(this);
          var tier = this.tiers[this.tierIdx];
          var code = (tier && tier.dataset.discountCode) || "";
          var counts = {};
          p.resolved.forEach(function (v) { counts[v.i] = (counts[v.i] || 0) + 1; });
          var items = Object.keys(counts).map(function (id) {
            return { variant_id: parseInt(id, 10), quantity: counts[id] };
          });
          if (!items.length) return origSubmit.call(this);
          hydraCheckout(items, { discount_code: code, value_cents: p.total || 0 }, function () {
            origSubmit.call(self);
          });
        } catch (err) {
          if (window.console) console.warn("[hydra] fish-bundle patch failed, using native:", err);
          return origSubmit.call(this);
        }
      };
      if (window.console) console.log("[hydra] fish-bundle checkout patched");
    });
  }
})();
